import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getAllCategories } from '@/lib/community';
import { usePageMeta } from '@/hooks/usePageMeta';
import { cn } from '@/lib/format';

const categories = getAllCategories();

export default function PostPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [content, setContent] = useState('');
  const [errors, setErrors] = useState<{ title?: string; category?: string; content?: string }>({});
  const [submitted, setSubmitted] = useState(false);
  const [imageHint, setImageHint] = useState(false);

  usePageMeta({
    title: '发布话题 - 蜜糖社区',
    description: '在蜜糖社区发布你的新话题，选择合适的分类，分享你的经验、观点和故事，和兴趣相投的人一起交流。',
    keywords: '发布话题,蜜糖社区,发帖,兴趣分享,社区投稿',
  });

  const selectedCategory = categories.find((c) => c.id === categoryId);

  const validate = () => {
    const next: typeof errors = {};
    if (!title.trim()) next.title = '请填写话题标题';
    else if (title.trim().length < 6) next.title = '标题至少 6 个字，说清楚你想聊什么';
    if (!categoryId) next.category = '请选择一个话题分类';
    if (!content.trim()) next.content = '正文内容不能为空';
    else if (content.trim().length < 20) next.content = '正文至少 20 个字，让读者更了解你的想法';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitted(true);
    window.setTimeout(() => navigate('/'), 1600);
  };

  const tips = ['开头一句话点明主题', '适当分段落，方便阅读', '配上图片会更吸引人', '结尾抛个问题，引导互动'];

  return (
    <Layout>
      <div className="w-full max-w-page mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '发布话题' }]} />

        <div className="mt-5 grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-10">
          <div className="lg:col-span-2">
            <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-900">发布新话题</h1>
            <p className="mt-2 text-sm text-foreground-500">分享你的经验、观点或故事，让更多同好看到。</p>

            <form onSubmit={handleSubmit} className="mt-6 bg-background-50 border border-background-200 rounded-lg p-5 md:p-7 flex flex-col gap-6">
              {/* Title */}
              <div>
                <label htmlFor="topic-title" className="block text-sm font-label font-bold text-foreground-900 mb-2">
                  话题标题 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="topic-title"
                  name="title"
                  value={title}
                  onChange={(e) => { setTitle(e.target.value); setErrors((p) => ({ ...p, title: undefined })); }}
                  maxLength={60}
                  placeholder="用一句话概括你想聊的话题"
                  className={cn(
                    'w-full rounded-md border bg-background-50 px-4 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors',
                    errors.title ? 'border-accent-400' : 'border-background-300 focus:border-primary-400',
                  )}
                />
                <div className="mt-1.5 flex items-center justify-between gap-3">
                  {errors.title ? (
                    <p className="text-xs text-accent-600 flex items-center gap-1"><i className="ri-error-warning-line" />{errors.title}</p>
                  ) : <span className="text-xs text-foreground-400">好的标题更容易被点开</span>}
                  <span className="text-xs text-foreground-400">{title.length}/60</span>
                </div>
              </div>

              {/* Category */}
              <div>
                <span className="block text-sm font-label font-bold text-foreground-900 mb-2">
                  选择分类 <span className="text-accent-500">*</span>
                </span>
                <div className="flex flex-wrap gap-2">
                  {categories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => { setCategoryId(cat.id); setErrors((p) => ({ ...p, category: undefined })); }}
                      className={cn(
                        'inline-flex items-center gap-1.5 rounded-md border px-3.5 py-2 text-sm font-label font-bold transition-colors cursor-pointer whitespace-nowrap',
                        categoryId === cat.id
                          ? 'border-primary-400 bg-primary-100 text-primary-800'
                          : 'border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300',
                      )}
                    >
                      <i className={cat.icon} />
                      {cat.name}
                    </button>
                  ))}
                </div>
                {errors.category && (
                  <p className="mt-2 text-xs text-accent-600 flex items-center gap-1"><i className="ri-error-warning-line" />{errors.category}</p>
                )}
                {selectedCategory && (
                  <div className="mt-3 rounded-md bg-background-100 border border-background-200 p-3.5 animate-fade-up">
                    <p className="text-sm font-bold text-foreground-800 flex items-center gap-1.5">
                      <i className={cn(selectedCategory.icon, 'text-primary-600')} />
                      {selectedCategory.name} · 适合发布这些内容
                    </p>
                    <p className="mt-1.5 text-xs text-foreground-600 leading-relaxed">{selectedCategory.description}</p>
                  </div>
                )}
              </div>

              {/* Content */}
              <div>
                <label htmlFor="topic-content" className="block text-sm font-label font-bold text-foreground-900 mb-2">
                  正文内容 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="topic-content"
                  name="content"
                  value={content}
                  onChange={(e) => { setContent(e.target.value); setErrors((p) => ({ ...p, content: undefined })); }}
                  rows={10}
                  maxLength={5000}
                  placeholder="详细描述你想分享的内容，可以分段落，让阅读更轻松…"
                  className={cn(
                    'w-full resize-none rounded-md border bg-background-50 px-4 py-3 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 outline-none transition-colors',
                    errors.content ? 'border-accent-400' : 'border-background-300 focus:border-primary-400',
                  )}
                />
                <div className="mt-1.5 flex items-center justify-between gap-3">
                  {errors.content ? (
                    <p className="text-xs text-accent-600 flex items-center gap-1"><i className="ri-error-warning-line" />{errors.content}</p>
                  ) : <span className="text-xs text-foreground-400">支持纯文本，建议 200 字以上</span>}
                  <span className="text-xs text-foreground-400">{content.length}/5000</span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setImageHint(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 text-foreground-600 text-xs font-label font-bold px-3 py-1.5 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-image-add-line" />
                    插入图片
                  </button>
                  <button
                    type="button"
                    onClick={() => setImageHint(true)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-background-300 text-foreground-600 text-xs font-label font-bold px-3 py-1.5 hover:border-primary-400 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-emotion-happy-line" />
                    插入表情
                  </button>
                  <span className="text-xs text-foreground-400">（图片与表情为前端演示功能）</span>
                </div>

                {imageHint && (
                  <div className="mt-3 rounded-md bg-secondary-100 border border-secondary-200 p-3.5 animate-fade-up flex items-start gap-2">
                    <i className="ri-information-line text-secondary-600 mt-0.5" />
                    <p className="text-xs text-secondary-800 leading-relaxed">
                      图片上传与表情插入为前端演示功能，当前版本不会真正上传文件。正式上线后可接入存储服务。
                    </p>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-2 border-t border-background-200">
                <button
                  type="button"
                  onClick={() => { setTitle(''); setCategoryId(''); setContent(''); setErrors({}); setImageHint(false); }}
                  className="inline-flex items-center gap-1.5 rounded-md border border-background-300 text-foreground-700 text-sm font-label font-bold px-5 py-2.5 hover:border-primary-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  清空重填
                </button>
                <button
                  type="submit"
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-6 py-2.5 whitespace-nowrap transition-colors cursor-pointer"
                >
                  <i className="ri-send-plane-fill" />
                  发布话题
                </button>
              </div>

              <p className="text-xs text-foreground-400 flex items-center gap-1.5">
                <i className="ri-information-line" />
                提交仅为前端演示，不会真正发布到服务器。
              </p>
            </form>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1 flex flex-col gap-5">
            <section className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-lightbulb-line text-primary-600" />
                <h2 className="text-base font-extrabold text-foreground-900">发帖小贴士</h2>
              </div>
              <ul className="space-y-2.5">
                {tips.map((tip, i) => (
                  <li key={tip} className="flex items-start gap-2.5 text-sm text-foreground-600">
                    <span className="w-5 h-5 rounded-md bg-primary-100 text-primary-700 text-[11px] font-label font-bold flex items-center justify-center shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </section>

            <section className="bg-background-50 border border-background-200 rounded-lg p-5">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-shield-check-line text-accent-600" />
                <h2 className="text-base font-extrabold text-foreground-900">社区公约</h2>
              </div>
              <ul className="space-y-2 text-sm text-foreground-600 leading-relaxed">
                <li>· 友善交流，尊重不同观点</li>
                <li>· 不发布违法违规或广告内容</li>
                <li>· 转载内容请注明出处</li>
                <li>· 理性讨论，共同维护社区氛围</li>
              </ul>
              <Link to="/about" className="mt-4 inline-flex items-center gap-1 text-sm font-label font-bold text-primary-700 hover:text-primary-800 transition-colors cursor-pointer whitespace-nowrap">
                查看完整公约
                <i className="ri-arrow-right-line" />
              </Link>
            </section>
          </aside>
        </div>
      </div>

      {submitted && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-foreground-950/40 backdrop-blur-sm" aria-hidden="true" />
          <div className="relative w-full max-w-sm bg-background-50 rounded-lg border border-background-200 p-7 text-center animate-fade-up">
            <span className="w-14 h-14 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto mb-4">
              <i className="ri-checkbox-circle-fill text-3xl" />
            </span>
            <h2 className="text-lg font-extrabold text-foreground-900">发布成功！</h2>
            <p className="mt-2 text-sm text-foreground-500 leading-relaxed">这是一条前端演示提示，我们正在带你返回首页…</p>
            <button
              type="button"
              onClick={() => navigate('/')}
              className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-label font-bold px-5 py-2.5 whitespace-nowrap transition-colors cursor-pointer"
            >
              立即返回首页
            </button>
          </div>
        </div>
      )}
    </Layout>
  );
}